import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import HighlightText from '../components/HighlightText';

const { FiPhone, FiMapPin, FiSend, FiUser, FiMessageSquare, FiClock, FiMessageCircle, FiMail, FiCheckCircle } = FiIcons;

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact MyHomeTutors Lucknow',
  description: 'Get in touch with MyHomeTutors Lucknow. Call, WhatsApp or fill the form to find a home tutor near you.',
  url: 'https://myhometutors.org/contact',
  publisher: {
    '@type': 'Organization',
    name: 'MyHomeTutors',
    telephone: '+919415905584',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'H.N 1/358, Vastu Khand, Gomti Nagar',
      addressLocality: 'Lucknow',
      addressRegion: 'Uttar Pradesh',
      postalCode: '226010',
      addressCountry: 'IN',
    },
  },
};

const contactCards = [
  {
    icon: FiPhone,
    title: 'Call Us Directly',
    value: '+91 9415905584',
    sub: 'Mon–Sat, 9 AM – 7 PM',
    href: 'tel:+919415905584',
    color: 'bg-blue-50 text-blue-600',
    btnLabel: 'Call Now',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: FiMessageCircle,
    title: 'WhatsApp Us',
    value: '+91 9415905584',
    sub: 'Quick replies on WhatsApp',
    href: 'https://wa.me/919415905584',
    color: 'bg-green-50 text-green-600',
    btnLabel: 'Open WhatsApp',
    btnClass: 'bg-green-500 hover:bg-green-600',
    external: true,
  },
  {
    icon: FiMapPin,
    title: 'Visit Our Office',
    value: 'H.N 1/358, Vastu Khand',
    sub: 'Gomti Nagar, Lucknow – 226010',
    href: 'https://www.google.com/maps?cid=6245695101345977885',
    color: 'bg-red-50 text-primary',
    btnLabel: 'Get Directions',
    btnClass: 'bg-primary hover:bg-primary-hover',
    external: true,
  },
  {
    icon: FiClock,
    title: 'Working Hours',
    value: 'Mon – Sat: 9 AM – 7 PM',
    sub: 'Sunday: 10 AM – 4 PM',
    color: 'bg-orange-50 text-orange-600',
  },
];

const ContactPageFull = () => {
  const [form, setForm] = useState({ name: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setForm({ name: '', phone: '', subject: '', message: '' });
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <div className="min-h-screen bg-[#fdf8f4]">
      <Helmet>
        <title>Contact Us | MyHomeTutors Lucknow – Find Home Tutors</title>
        <meta name="description" content="Contact MyHomeTutors Lucknow. Call +91 9415905584 or WhatsApp us to find the best home tutor near you. Office at Gomti Nagar, Lucknow." />
        <link rel="canonical" href="https://myhometutors.org/contact/" />
        <meta property="og:title" content="Contact Us | MyHomeTutors Lucknow – Find Home Tutors" />
        <meta property="og:description" content="Contact MyHomeTutors Lucknow. Call or WhatsApp to find the best home tutor near you." />
        <meta property="og:url" content="https://myhometutors.org/contact" />
        <meta property="og:type" content="website" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#fff5f5] via-[#fff8f8] to-[#ffeaea] py-14 lg:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#fd6c6b 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white text-primary text-xs font-bold px-4 py-2 rounded-full border border-red-100 shadow-sm uppercase tracking-widest mb-5">
            <FiPhone className="w-3.5 h-3.5" /> We're Here to Help
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            Get In <HighlightText>Touch</HighlightText> With Us
          </h1>
          <p className="text-lg text-gray-600 font-medium max-w-2xl mx-auto">
            Looking for a home tutor in Lucknow? Have a question? We're just a call or message away. Our team responds within 30 minutes.
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10 pb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {contactCards.map((c, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col">
              <div className={`w-12 h-12 rounded-xl ${c.color} flex items-center justify-center mb-4`}>
                <c.icon className="w-6 h-6" />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-1 text-sm">{c.title}</h3>
              <p className="font-bold text-slate-800 text-sm mb-0.5">{c.value}</p>
              <p className="text-xs text-gray-500 font-medium mb-4 flex-1">{c.sub}</p>
              {c.href && (
                <a href={c.href} target={c.external ? '_blank' : undefined} rel={c.external ? 'noopener noreferrer' : undefined}
                  className={`text-center text-white text-xs font-extrabold py-2.5 rounded-xl transition shadow-sm ${c.btnClass}`}>
                  {c.btnLabel}
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Form + Map */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Form */}
          <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary via-orange-400 to-yellow-400 rounded-t-3xl" />
            <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Send Us a Message</h2>
            <p className="text-sm text-gray-500 font-medium mb-6">Fill the form and our team will reach out within 30 minutes.</p>

            {submitted && (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-5">
                <FiCheckCircle className="text-green-500 w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-bold text-green-700">Thank you! We'll contact you shortly.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {[
                { name: 'name', placeholder: 'Your Full Name', icon: FiUser },
                { name: 'phone', placeholder: 'Phone Number', icon: FiPhone },
                { name: 'subject', placeholder: 'Subject (e.g., Math Tutor for Class 10)', icon: FiMail },
              ].map((f) => (
                <div key={f.name} className="relative">
                  <f.icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" name={f.name} value={form[f.name]} onChange={handleChange}
                    placeholder={f.placeholder}
                    className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 placeholder-gray-400 font-medium text-slate-700" />
                </div>
              ))}
              <div className="relative">
                <FiMessageSquare className="absolute left-4 top-4 w-4 h-4 text-gray-400" />
                <textarea name="message" value={form.message} onChange={handleChange}
                  placeholder="Your message or requirement..."
                  rows={4}
                  className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 placeholder-gray-400 font-medium text-slate-700 resize-none" />
              </div>
              <button type="submit" className="w-full bg-primary text-white py-4 rounded-xl font-extrabold hover:bg-primary-hover transition-all shadow-lg flex items-center justify-center gap-2 tracking-wide">
                <FiSend className="w-4 h-4" /> Send Message
              </button>
            </form>
          </div>

          {/* Map + Info */}
          <div className="space-y-6">
            <div className="rounded-3xl overflow-hidden border border-gray-100 shadow-sm h-64 lg:h-80">
              <iframe title="MyHomeTutors Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3559.3!2d80.9!3d26.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x56b5e5c3b4e2f21d!2sMyHomeTutors!5e0!3m2!1sen!2sin!4v1!5m2!1sen!2sin"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" />
            </div>
            <div className="bg-white rounded-3xl p-7 border border-gray-100 shadow-sm">
              <h3 className="font-extrabold text-slate-900 text-lg mb-5">Our Office Address</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FiMapPin className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">H.N 1/358, Vastu Khand, Gomti Nagar</p>
                    <p className="text-sm text-gray-500 font-medium">Lucknow, Uttar Pradesh 226010</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FiPhone className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <a href="tel:+919415905584" className="font-bold text-slate-800 text-sm hover:text-primary transition">+91 9415905584</a>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">Mon–Sat 9 AM to 7 PM</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FiMessageCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <a href="https://wa.me/919415905584" target="_blank" rel="noopener noreferrer" className="font-bold text-slate-800 text-sm hover:text-green-600 transition">WhatsApp: +91 9415905584</a>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">Quick replies guaranteed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPageFull;